import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Box,
  Stack,
  IconButton,
  Typography,
  CircularProgress,
  useMediaQuery,
} from '@mui/material';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { renderPdfToContainer } from '../lib/pdfjs';

const DOCX_MIME = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

function isDocx(mimeType) {
  return mimeType === DOCX_MIME;
}

const MIN_ZOOM = 0.25;
const MAX_ZOOM = 4;
const ZOOM_STEP = 0.25;

function previewItems(preview) {
  if (!preview) return [];
  if (Array.isArray(preview.items) && preview.items.length > 0) return preview.items;
  if (preview.url || preview.key) return [preview];
  return [];
}

export default function FilePreviewDialog({
  preview,
  onClose,
  request,
  contentPath,
}) {
  const isCoarsePointer = useMediaQuery('(pointer: coarse)');
  const [entered, setEntered] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [blobUrl, setBlobUrl] = useState(null);
  const [pdfBuffer, setPdfBuffer] = useState(null);
  const [usePdfCanvas, setUsePdfCanvas] = useState(false);
  const pdfContainerRef = useRef(null);
  const pdfRenderIdRef = useRef(0);

  const items = previewItems(preview);
  const current = items.length > 0 ? items[activeIndex] : preview;
  const canGoPrev = items.length > 1 && activeIndex > 0;
  const canGoNext = items.length > 1 && activeIndex < items.length - 1;

  const isImagePreview = current?.isImage;
  const isDocxPreview = current && !current.isImage && isDocx(current.mimeType);
  const isPdfPreview = current && !current.isImage && !isDocx(current.mimeType);

  useEffect(() => {
    if (!preview) {
      setEntered(false);
      setActiveIndex(0);
      setBlobUrl(null);
      setPdfBuffer(null);
      setUsePdfCanvas(false);
      setError('');
      setLoading(false);
      setZoom(1);
      return;
    }
    const list = previewItems(preview);
    let idx = typeof preview.index === 'number' ? preview.index : 0;
    if (idx < 0) idx = 0;
    if (idx >= list.length) idx = list.length - 1;
    setActiveIndex(idx);
  }, [preview]);

  useEffect(() => {
    setZoom(1);
  }, [current?.url, current?.key]);

  useEffect(() => {
    if (!current || !entered) return undefined;
    let cancelled = false;
    let createdUrl = null;

    async function loadDocument() {
      if (isImagePreview || isDocxPreview) {
        setLoading(false);
        return;
      }
      if (!isPdfPreview) return;

      setLoading(true);
      setError('');
      setBlobUrl(null);
      setPdfBuffer(null);
      setUsePdfCanvas(false);

      try {
        let buffer;
        if (current.url && current.url.startsWith('blob:')) {
          const res = await fetch(current.url);
          buffer = await res.arrayBuffer();
        } else if (current.key && request && contentPath) {
          const res = await request(`${contentPath}?key=${encodeURIComponent(current.key)}`);
          if (!res.ok) throw new Error('fetch failed');
          buffer = await res.arrayBuffer();
        } else {
          throw new Error('missing source');
        }
        if (cancelled) return;

        if (isCoarsePointer) {
          setPdfBuffer(buffer.slice(0));
          setUsePdfCanvas(true);
        } else {
          createdUrl = URL.createObjectURL(new Blob([buffer], { type: 'application/pdf' }));
          setBlobUrl(createdUrl);
          createdUrl = null;
        }
      } catch (e) {
        if (!cancelled) setError('Could not load preview');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadDocument();
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [current, entered, request, contentPath, isCoarsePointer, isImagePreview, isDocxPreview, isPdfPreview]);

  useEffect(() => {
    if (!usePdfCanvas || !pdfBuffer || !entered || loading) return undefined;
    let cancelled = false;
    const renderId = pdfRenderIdRef.current + 1;
    pdfRenderIdRef.current = renderId;

    async function renderMobilePdf() {
      const container = pdfContainerRef.current;
      if (!container) return;
      container.innerHTML = '';
      try {
        if (cancelled || pdfRenderIdRef.current !== renderId) return;
        await renderPdfToContainer(container, pdfBuffer);
      } catch (e) {
        if (!cancelled) setError('Could not load preview');
      }
    }

    renderMobilePdf();
    return () => { cancelled = true; };
  }, [usePdfCanvas, pdfBuffer, entered, loading]);

  useEffect(() => {
    return () => {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [blobUrl]);

  function goPrev() {
    setActiveIndex((i) => Math.max(0, i - 1));
  }

  function goNext() {
    setActiveIndex((i) => Math.min(items.length - 1, i + 1));
  }

  function zoomIn() {
    setZoom((z) => Math.min(MAX_ZOOM, Math.round((z + ZOOM_STEP) * 100) / 100));
  }

  function zoomOut() {
    setZoom((z) => Math.max(MIN_ZOOM, Math.round((z - ZOOM_STEP) * 100) / 100));
  }

  function resetZoom() {
    setZoom(1);
  }

  const viewerBoxSx = {
    width: '100%',
    height: '70vh',
    minHeight: 400,
    position: 'relative',
    border: 1,
    borderColor: 'divider',
    borderRadius: 1,
    bgcolor: 'action.hover',
    overflow: isImagePreview ? 'auto' : 'hidden',
    ...(isImagePreview
      ? { display: 'flex', alignItems: 'center', justifyContent: 'center' }
      : {}),
  };

  return (
    <Dialog
      open={!!preview}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      TransitionProps={{
        onEntered: () => setEntered(true),
        onExited: () => setEntered(false),
      }}
      PaperProps={{
        sx: {
          width: { xs: '98%', sm: '100%' },
          maxWidth: { xs: '98%', sm: 900 },
          m: { xs: 1 },
        },
      }}
    >
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Button size="small" onClick={onClose}>
              Close preview
            </Button>
            {items.length > 1 && (
              <Typography variant="body2" color="text.secondary">
                {activeIndex + 1} / {items.length}
              </Typography>
            )}
          </Stack>
          {isImagePreview && (
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <IconButton size="small" onClick={zoomOut} disabled={zoom <= MIN_ZOOM} aria-label="Zoom out">
                <ZoomOutIcon fontSize="small" />
              </IconButton>
              <Typography variant="body2" sx={{ minWidth: 48, textAlign: 'center' }}>
                {Math.round(zoom * 100)}%
              </Typography>
              <IconButton size="small" onClick={zoomIn} disabled={zoom >= MAX_ZOOM} aria-label="Zoom in">
                <ZoomInIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={resetZoom} aria-label="Reset zoom">
                <RestartAltIcon fontSize="small" />
              </IconButton>
            </Stack>
          )}
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Box sx={viewerBoxSx}>
          {items.length > 1 && (
            <>
              <IconButton
                onClick={goPrev}
                disabled={!canGoPrev}
                aria-label="Previous file"
                sx={{
                  position: 'absolute',
                  left: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 2,
                  bgcolor: 'background.paper',
                  boxShadow: 1,
                  '&:hover': { bgcolor: 'background.paper' },
                  '&.Mui-disabled': { opacity: 0.35 },
                }}
              >
                <ChevronLeftIcon />
              </IconButton>
              <IconButton
                onClick={goNext}
                disabled={!canGoNext}
                aria-label="Next file"
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 2,
                  bgcolor: 'background.paper',
                  boxShadow: 1,
                  '&:hover': { bgcolor: 'background.paper' },
                  '&.Mui-disabled': { opacity: 0.35 },
                }}
              >
                <ChevronRightIcon />
              </IconButton>
            </>
          )}
          {loading && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1,
              }}
            >
              <CircularProgress />
            </Box>
          )}
          {error && !loading && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 2,
              }}
            >
              <Typography color="error">{error}</Typography>
            </Box>
          )}
          {entered && isImagePreview && current.url && (
            <Box
              sx={{
                transform: `scale(${zoom})`,
                transformOrigin: 'center center',
                flexShrink: 0,
              }}
            >
              <Box
                component="img"
                src={current.url}
                alt="Preview"
                sx={{ maxWidth: '100%', maxHeight: '65vh', objectFit: 'contain', display: 'block' }}
              />
            </Box>
          )}
          {entered && isDocxPreview && current.url && !loading && (
            <Box
              component="iframe"
              src={`https://docs.google.com/viewer?url=${encodeURIComponent(current.url)}&embedded=true`}
              title="Document preview"
              sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }}
            />
          )}
          {entered && isPdfPreview && usePdfCanvas && !loading && !error && (
            <Box
              ref={pdfContainerRef}
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                overflow: 'auto',
                p: 1,
                boxSizing: 'border-box',
              }}
            />
          )}
          {entered && isPdfPreview && !usePdfCanvas && blobUrl && !loading && !error && (
            <Box
              component="iframe"
              key={blobUrl}
              src={blobUrl}
              title="File preview"
              sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }}
            />
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
}
